/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import TableHead from '@mui/material/TableHead';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Switch from '@mui/material/Switch';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import CheckCost from './CheckCost';

const initParent = {
  id: '',
  code: '',
  remark: '',
  type_id: '',
  supplier_id: '',
  discount_type: 'B',
  discount_value: '',
  shipping: '',
  discount_unit: 'Y',
  vat_id: '2',
  status: 'edit',
  print_url: '',
  created_at: '',
  updated_at: '',
  checkall: 'N',
};

function ReceiveFixedCost({ callback }) {
  const mod = 'receive_list';
  const app = useSelector((state) => state[APP_STATE]);
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [confirm, setConfirm] = useState(false);
  const [record, setRecord] = useState([]);
  const [parent, setParent] = useState(initParent);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [price, setPrice] = useState(0);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const view = () => {
    setLoading(true);

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      ref_id: id,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setParent(result.parent);
        setPrice(result.price);
      }
      setLoading(false);
    });
  };

  const checkAll = (inp) => {
    const check = inp ? 'Y' : 'N';
    setParent({ ...parent, checkall: check });
    FetchData('OPTIONS', `/${mod}/checkall`, app.token, {
      lang: app.lang,
      id: id,
      checkall: check,
    }).then((result) => {
      if (result.status === 'ok') {
        setPrice(result.price);
        view();
      }
    });
  };

  const checkList = (item) => {
    FetchData('OPTIONS', `/${mod}/checklist`, app.token, {
      id: item.id,
      ref_id: id,
      checklist: item.checklist,
    }).then((result) => {
      if (result.status === 'ok') {
        setPrice(result.price);
      }
    });
  };

  const edit = () => {
    setLoading(true);
    setConfirm(false);

    FetchData('OPTIONS', `/${mod}/fixedcost`, app.token, {
      id: id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        callback();
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const cancel = () => {
    setLoading(true);
    setConfirm(false);

    FetchData('OPTIONS', `/${mod}/cancelcost`, app.token, {
      id: id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        callback();
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const renderHeader = () => {
    return (
      <TableHead>
        <TableRow>
          <TableCell align='center' width={10}>
            No.
          </TableCell>
          <TableCell align='center' width={40}>
            รูป
          </TableCell>
          <TableCell>SKU</TableCell>
          <TableCell>สินค้า</TableCell>
          <TableCell align='right' width={90} className='nowrap'>
            จำนวน
          </TableCell>
          <TableCell align='right' width={90} className='nowrap'>
            แถม
          </TableCell>
          <TableCell align='right' width={80} className='nowrap'>
            ราคารับ
          </TableCell>
          <TableCell align='right' width={80} className='nowrap'>
            ราคาซื้อ
          </TableCell>
          <TableCell align='right' width={80} className='nowrap'>
            ส่วนต่าง
          </TableCell>
          <TableCell align='right' width={80} className='nowrap'>
            ส่วนลด
          </TableCell>
          <TableCell align='right' width={80} className='nowrap'>
            จำนวนเงิน
          </TableCell>
          <TableCell align='right' width={80} className='nowrap'>
            ปันทุน
          </TableCell>
          <TableCell align='center' width={80}>
            <Switch
              checked={parent.checkall === 'Y'}
              onChange={(e) => checkAll(e.target.checked)}
              inputProps={{ 'aria-label': 'controlled' }}
              color='info'
            />
          </TableCell>
        </TableRow>
      </TableHead>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TableContainer sx={{ boxShadow: 0, minHeight: 500 }}>
              <Table aria-label='simple table'>
                {renderHeader()}
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center'>{row.no}</TableCell>
                      <TableCell>{Func.imageDisplay(row)}</TableCell>
                      <TableCell>
                        <MDBox>{row.barcode}</MDBox>
                        <MDBox>{row.barcode2}</MDBox>
                      </TableCell>
                      <TableCell>
                        <MDBox>{row.code}</MDBox>
                        <MDBox className='nowrap'>{row.name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(row.quantity_receive)}</MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox>{Func.numberDisplay(row.quantity_free)}</MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>{row.price}</TableCell>
                      <TableCell align='right'>{row.price_po}</TableCell>
                      <TableCell align='right'>{row.price_diff}</TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.discount)}</TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.amount)}</TableCell>
                      <TableCell align='right' className='color-danger'>
                        {Func.numberDisplay(row.fixed_cost)}
                      </TableCell>
                      <TableCell align='center'>
                        <CheckCost parent={row} callback={checkList} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TableContainer sx={{ boxShadow: 0, mt: 2 }}>
              <Table aria-label='summary table'>
                <TableBody>
                  <TableRow>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>มูลค่าสินค้า</MDTypography>
                    </TableCell>
                    <TableCell align='right' width='200'>
                      <MDTypography variant='h5'>
                        {Func.numberDisplay(parent.total, 2)}
                      </MDTypography>
                    </TableCell>
                    <TableCell align='right' width={50}>
                      <MDTypography variant='h5'>บาท</MDTypography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>หักส่วนลด</MDTypography>
                    </TableCell>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>
                        {Func.numberDisplay(parent.discount, 2)}
                      </MDTypography>
                    </TableCell>
                    <TableCell align='right' width={50}>
                      <MDTypography variant='h5'>บาท</MDTypography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>มูลค่าสินค้าหลังหักส่วนลด</MDTypography>
                    </TableCell>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>
                        {Func.numberDisplay(parent.amount, 2)}
                      </MDTypography>
                    </TableCell>
                    <TableCell align='right' width={50}>
                      <MDTypography variant='h5'>บาท</MDTypography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>ค่าจัดส่ง</MDTypography>
                    </TableCell>
                    <TableCell align='right'>
                      <MDTypography variant='h5'>
                        {Func.numberDisplay(parent.shipping, 2)}
                      </MDTypography>
                    </TableCell>
                    <TableCell align='right' width={50}>
                      <MDTypography variant='h5'>บาท</MDTypography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth='xxl'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={() => setModalOpen(false)}
      >
        <DialogTitle>
          ปันทุน
          <IconButton
            aria-label='close'
            onClick={() => setModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderList()}</DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={edit}>
                  <Icon>save</Icon>&nbsp;ยืนยันการปันทุน
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={cancel}>
                  <Icon>cancel</Icon>&nbsp;ยกเลิกปันทุน
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='text' color='success' onClick={() => setConfirm(true)}>
                  <Icon>save</Icon>&nbsp;ปันทุน
                </MDButton>
              </Grid>
            </Grid>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <div>
      <MDButton
        variant='gradient'
        color='primary'
        onClick={() => {
          setModalOpen(true);
          view();
        }}
      >
        <Icon>currency_exchange</Icon>&nbsp;ปันทุน
      </MDButton>

      {renderOpen()}
      {notify}
    </div>
  );
}

ReceiveFixedCost.propTypes = {
  callback: PropTypes.func.isRequired,
};

export default ReceiveFixedCost;
